<template>
  <div class="main-wrapper">
    <div class="menu">
      <button @click="toggleLayout">Zmień Układ (Prawo/Dół)</button>
      <small>Przeciągnij ciemny pasek, aby zmienić rozmiar</small>
    </div>

    <div id="main-container" :class="[layoutMode, { 'resizing': isResizing }]">
      <!-- PANEL 1: WYKRESY -->
      <div id="pane-1" class="pane">
        <Spinner v-if="trackDetailsLoading" height="50px"></Spinner>
        <div id="chart_speed"></div>
        <div id="chart_cadence"></div>
        <div id="chart_hr"></div>
        <div id="chart_pwr"></div>
        <Spinner v-if="trackHeightLoading" height="50px"></Spinner>
        <div id="chart_height"></div>
      </div>

      <!-- UCHWYT (RESIZER) -->
      <div id="resizer" @mousedown="startResizing"></div>

      <!-- PANEL 2: DODATKOWY -->
      <div id="pane-2" class="pane" :style="pane2Style">
        <div class="content">
          <h3>Panel boczny</h3>
          <p>Treść drugiego diva...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Highcharts from 'highcharts';
import axios from 'axios';
import { AlertStatus } from '@/ts/types';
import BaseComponent from '@/components/Base.vue';
import { defineComponent, markRaw } from "vue";

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      layoutMode: 'layout-row', // 'layout-row' lub 'layout-column'
      isResizing: false,
      pane2Size: 300, // Szerokość lub wysokość w pikselach
      charts: markRaw([]),      // Przechowuje instancje Highcharts

      trackDetailsLoading: true,
      trackHeightLoading: true,
      track: undefined as { json_file: string },
      legend: markRaw([]) as number[],
      speed: markRaw([]) as number[],
      cadence: markRaw([]) as number[],
      hr: markRaw([]) as number[],
      pwr: markRaw([]) as number[],
      heights: markRaw([]) as number[],
    };
  },
  computed: {
    pane2Style() {
      if (this.layoutMode === 'layout-row') {
        return { width: `${this.pane2Size}px`, height: '100%' };
      } else {
        return { height: `${this.pane2Size}px`, width: '100%' };
      }
    }
  },
  mounted() {
    this.initCharts();
    window.addEventListener('resize', this.reflowCharts);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.reflowCharts);
    // Niszczymy wykresy, aby zwolnić pamięć
    this.charts.forEach(c => c.destroy());
  },
  methods: {
    initCharts() {
      this.trackDetailsLoading = true;
      const url = window.location.hostname === 'localhost' ? 'http://localhost:8000/djangoapp/' : '/djangoapp/'
      axios.get(url + 'api/tracks/' + this.$route.params.id + '/').then((response) => {
        this.track = response.data;
        const json_file = JSON.parse(this.track.details.json_file);
        for (const point of json_file['details']) {
          this.legend.push(point.timestamp / 1000);
          this.speed.push(Math.round(point.gpsSpd * 3.6 * 10) / 10);
          this.cadence.push(Math.round(point.cad));
          this.hr.push(point.hr);
          this.pwr.push(point.pwr);
        }
        const c1 = Highcharts.chart('chart_speed', {
          title: {
            text: 'Prędkość',
          },
          yAxis: {
            title: {
              text: 'km / h',
            }
          },
          series: [{
            name: 'Sekundy',
            data: this.speed,
            color: 'red',
          }],
        });
        const c2 = Highcharts.chart('chart_cadence', {
          title: {
            text: 'Kadencja',
          },
          yAxis: {
            title: {
              text: 'obroty / min',
            }
          },
          series: [{
            name: 'Sekundy',
            data: this.cadence,
            color: 'blue',
          }],
        });
        const c3 = Highcharts.chart('chart_hr', {
          title: {
            text: 'Tętno',
          },
          yAxis: {
            title: {
              text: 'uderzenia / sec',
            }
          },
          series: [{
            name: 'Sekundy',
            data: this.hr,
            color: 'green',
          }],
        });
        const c4 = Highcharts.chart('chart_pwr', {
          title: {
            text: 'Moc',
          },
          yAxis: {
            title: {
              text: 'Waty',
            }
          },
          series: [{
            name: 'Sekundy',
            data: this.pwr,
            color: 'purple',
          }],
        });
        this.charts.push(c1);
        this.charts.push(c2);
        this.charts.push(c3);
        this.charts.push(c4);
      }).catch(() => {
        this.createAlert(AlertStatus.danger, this.$t('trackError').toString(), 2000);
      }).finally(() => {
        this.trackDetailsLoading = false;
      })

      this.trackHeightLoading = true;
      axios.get(url + 'api/measureheightfortrack/' + this.$route.params.id + '/').then((response) => {
        for (const segment of response.data.segments) {
          for (const height of segment) {
            this.heights.push(height / 100);
          }
        }
        const c5 = Highcharts.chart('chart_height', {
          title: {
            text: 'Wysokość',
          },
          yAxis: {
            title: {
              text: 'Metry',
            }
          },
          series: [{
            name: 'Metry',
            data: this.heights,
            color: 'purple',
          }],
        });
        this.charts.push(c5);
      }).catch(() => {
        this.createAlert(AlertStatus.danger, this.$t('trackError').toString(), 2000);
      }).finally(() => {
        this.trackHeightLoading = false;
      })
    },
    toggleLayout() {
      this.layoutMode = this.layoutMode === 'layout-row' ? 'layout-column' : 'layout-row';
      this.pane2Size = 300; // Reset rozmiaru przy zmianie układu
      this.$nextTick(this.reflowCharts);
    },
    startResizing(e) {
      this.isResizing = true;
      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.stopResizing);
      e.preventDefault();
    },
    handleMouseMove(e) {
      if (!this.isResizing) return;

      if (this.layoutMode === 'layout-row') {
        // Obliczamy szerokość od prawej krawędzi okna
        const newSize = window.innerWidth - e.clientX;
        if (newSize > 100 && newSize < window.innerWidth - 200) {
          this.pane2Size = newSize;
        }
      } else {
        // Obliczamy wysokość od dolnej krawędzi okna
        const newSize = window.innerHeight - e.clientY;
        if (newSize > 100 && newSize < window.innerHeight - 150) {
          this.pane2Size = newSize;
        }
      }
      this.reflowCharts();
    },
    stopResizing() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.stopResizing);
    },
    reflowCharts() {
      this.charts.forEach(chart => {
        if (chart) chart.reflow();
      });
    }
  }
});
</script>

<style scoped>
.main-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
}

.menu {
  height: 50px;
  background: #2c3e50;
  color: white;
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 15px;
}

#main-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

#main-container.layout-row {
  flex-direction: row;
}

#main-container.layout-column {
  flex-direction: column;
}

.pane {
  overflow-y: auto;
  background: white;
}

#pane-1 {
  flex: 1;
  padding: 20px;
}

#pane-2 {
  background: #fdfdfd;
  border: 1px solid #eee;
}

#resizer {
  background: #34495e;
  transition: background 0.1s;
  z-index: 10;
}

#resizer:hover {
  background: #3498db;
}

.layout-row #resizer {
  width: 8px;
  cursor: col-resize;
}

.layout-column #resizer {
  height: 8px;
  cursor: row-resize;
}

.chart-box {
  width: 100%;
  margin-bottom: 20px;
}

.resizing {
  user-select: none;
  cursor: inherit !important;
}

.spinner {
  text-align: center;
  padding: 10px;
  color: #666;
}

.content {
  padding: 15px;
}
</style>
